import { useState } from 'react';
import { BatteryCharging, Zap, Wrench, ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CollapsibleSection } from './CollapsibleSection';
import type { FleetPropertyRow } from '@/hooks/useFleetAnalytics';

interface ActionItemsSectionProps {
  properties: FleetPropertyRow[];
  periodDays: number;
}

interface ActionProperty {
  id: string;
  name: string;
  curtailed_kwh: number;
  curtailment_days: number;
  degradation_pct: number | null;
  rate_cents: number;
  monthly_impact_dollars: number;
}

interface ActionGroup {
  type: 'battery_expansion' | 'battery_replacement' | 'chronic_curtailment';
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  properties: ActionProperty[];
}

const MAX_VISIBLE = 5;

function monthlyDollars(curtailedKwh: number, periodDays: number, rateCents: number): number {
  if (periodDays <= 0) return 0;
  return (curtailedKwh / periodDays) * 30 * rateCents / 100;
}

export function ActionItemsSection({ properties, periodDays }: ActionItemsSectionProps) {
  // Build groups with deduplication: battery_expansion suppresses chronic_curtailment
  const expansionIds = new Set<string>();
  const groups: ActionGroup[] = [];

  // 1. Battery Expansion — most actionable
  const expansionProps: ActionProperty[] = [];
  for (const p of properties) {
    if (p.flag_battery_expansion) {
      expansionIds.add(p.id);
      const monthly = monthlyDollars(p.curtailed_kwh, periodDays, p.rate_cents);
      expansionProps.push({
        id: p.id,
        name: p.name,
        curtailed_kwh: p.curtailed_kwh,
        curtailment_days: p.curtailment_days,
        degradation_pct: p.degradation_pct,
        rate_cents: p.rate_cents,
        monthly_impact_dollars: monthly,
      });
    }
  }
  if (expansionProps.length > 0) {
    expansionProps.sort((a, b) => b.monthly_impact_dollars - a.monthly_impact_dollars);
    const totalMonthly = expansionProps.reduce((s, p) => s + p.monthly_impact_dollars, 0);
    groups.push({
      type: 'battery_expansion',
      icon: <BatteryCharging className="w-4 h-4 text-wt-amber-primary" />,
      title: `${expansionProps.length} propert${expansionProps.length === 1 ? 'y needs' : 'ies need'} more battery`,
      subtitle: `~$${totalMonthly.toFixed(0)}/mo wasted solar could be captured`,
      properties: expansionProps,
    });
  }

  // 2. Battery Replacement
  const replacementProps: ActionProperty[] = [];
  for (const p of properties) {
    if (p.flag_battery_replacement) {
      replacementProps.push({
        id: p.id,
        name: p.name,
        curtailed_kwh: p.curtailed_kwh,
        curtailment_days: p.curtailment_days,
        degradation_pct: p.degradation_pct,
        rate_cents: p.rate_cents,
        monthly_impact_dollars: 0,
      });
    }
  }
  if (replacementProps.length > 0) {
    replacementProps.sort((a, b) => (b.degradation_pct ?? 0) - (a.degradation_pct ?? 0));
    groups.push({
      type: 'battery_replacement',
      icon: <Wrench className="w-4 h-4 text-wt-red-primary" />,
      title: `${replacementProps.length} propert${replacementProps.length === 1 ? 'y' : 'ies'} approaching warranty threshold`,
      subtitle: 'Degradation exceeds 15%',
      properties: replacementProps,
    });
  }

  // 3. Chronic Curtailment — deduplicated against battery_expansion
  const curtailmentProps: ActionProperty[] = [];
  for (const p of properties) {
    if (p.flag_chronic_curtailment && !expansionIds.has(p.id)) {
      const monthly = monthlyDollars(p.curtailed_kwh, periodDays, p.rate_cents);
      curtailmentProps.push({
        id: p.id,
        name: p.name,
        curtailed_kwh: p.curtailed_kwh,
        curtailment_days: p.curtailment_days,
        degradation_pct: p.degradation_pct,
        rate_cents: p.rate_cents,
        monthly_impact_dollars: monthly,
      });
    }
  }
  if (curtailmentProps.length > 0) {
    curtailmentProps.sort((a, b) => b.monthly_impact_dollars - a.monthly_impact_dollars);
    const totalMonthly = curtailmentProps.reduce((s, p) => s + p.monthly_impact_dollars, 0);
    groups.push({
      type: 'chronic_curtailment',
      icon: <Zap className="w-4 h-4 text-wt-amber-primary" />,
      title: `${curtailmentProps.length} propert${curtailmentProps.length === 1 ? 'y has' : 'ies have'} chronic curtailment`,
      subtitle: totalMonthly > 0 ? `~$${totalMonthly.toFixed(0)}/mo impact` : 'Curtailment on most days',
      properties: curtailmentProps,
    });
  }

  const totalItems = groups.reduce((s, g) => s + g.properties.length, 0);

  // No action items — hide the section entirely
  if (groups.length === 0) return null;

  // Build collapsed summary text
  const collapsedLines = groups.map((g) => {
    const icon = g.type === 'battery_expansion' ? '⚡' : g.type === 'battery_replacement' ? '🔋' : '⚠️';
    return `${icon} ${g.title}`;
  });

  return (
    <CollapsibleSection
      id="action-items"
      title="Action Items"
      badge={totalItems}
      collapsedSummary={collapsedLines.join(' · ')}
      defaultExpanded
    >
      <div className="space-y-5">
        {groups.map((group) => (
          <ActionGroupView key={group.type} group={group} periodDays={periodDays} />
        ))}
      </div>
    </CollapsibleSection>
  );
}

function ActionGroupView({ group, periodDays }: { group: ActionGroup; periodDays: number }) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? group.properties : group.properties.slice(0, MAX_VISIBLE);
  const remaining = group.properties.length - MAX_VISIBLE;

  return (
    <div>
      {/* Group header */}
      <div className="flex items-center gap-2 mb-2">
        {group.icon}
        <span className="text-sm font-medium text-foreground">{group.title}</span>
        <span className="text-xs text-muted-foreground">— {group.subtitle}</span>
      </div>

      {/* Description for chronic curtailment */}
      {group.type === 'chronic_curtailment' && (
        <p className="text-xs text-muted-foreground mb-2 ml-6">
          Curtailment detected on most days. Systems may benefit from load shifting or additional storage.
        </p>
      )}

      {/* Property rows */}
      <div className="rounded-md border border-border/50 overflow-hidden">
        {visible.map((p, i) => (
          <div
            key={p.id}
            className={`flex items-center gap-3 px-3 py-2 text-sm hover:bg-wt-bg-tertiary/50 ${
              i < visible.length - 1 ? 'border-b border-border/30' : ''
            }`}
          >
            <span className="text-foreground min-w-[140px] truncate">{p.name}</span>

            {group.type === 'battery_replacement' ? (
              <>
                <span className="font-mono text-wt-red-primary text-xs">
                  {p.degradation_pct?.toFixed(1)}% degradation
                </span>
                <span className="text-xs text-muted-foreground ml-auto mr-2">
                  replacement may be warranted
                </span>
              </>
            ) : (
              <>
                <span className="font-mono text-xs text-muted-foreground">
                  {p.curtailed_kwh.toFixed(0)} kWh
                </span>
                {periodDays > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {p.curtailment_days} of {periodDays} days
                  </span>
                )}
                <span className="font-mono text-xs text-wt-amber-primary ml-auto mr-2">
                  ~${p.monthly_impact_dollars.toFixed(0)}/mo wasted
                </span>
              </>
            )}

            <Link
              to={`/app/dashboard?property=${p.id}`}
              className="text-wt-green-primary hover:underline text-xs flex items-center gap-1 shrink-0"
            >
              View <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        ))}

        {/* Show more */}
        {remaining > 0 && !showAll && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="w-full px-3 py-2 text-xs text-wt-green-primary hover:bg-wt-bg-tertiary/50 text-left border-t border-border/30"
          >
            + {remaining} more propert{remaining === 1 ? 'y' : 'ies'}
          </button>
        )}
      </div>
    </div>
  );
}

/** Green empty state — used when there are truly zero action items (called externally). */
export function NoActionItems() {
  return (
    <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-border bg-wt-bg-secondary">
      <CheckCircle className="w-4 h-4 text-wt-green-primary" />
      <span className="text-sm text-wt-green-primary">
        No action items this period. All properties performing within normal ranges.
      </span>
    </div>
  );
}
